import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { forwardRef, InputHTMLAttributes, useState } from "react";
import { Eye, EyeOff, Lock } from "lucide-react";
import { cn } from "@/lib/utils";

interface PasswordFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
  error?: string;
  showIcon?: boolean;
}

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  ({ label, error, showIcon = true, className, id, ...props }, ref) => {
  useLanguage();
    const [showPassword, setShowPassword] = useState(false);
    const [capsLockOn, setCapsLockOn] = useState(false);
    const inputId = id || "password";

    // Detect Caps Lock
    const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
      setCapsLockOn(e.getModifierState('CapsLock'));
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      setCapsLockOn(e.getModifierState('CapsLock'));
    };

    return (
      <div className="space-y-2">
        <label
          htmlFor={inputId}
          className="block text-[12px] sm:text-[13px] font-semibold"
          style={{ color: '#0B1220' }}
        >
          {label}
        </label>
        <div className="relative">
          {showIcon && (
            <div
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            >
              <Lock className="h-4 w-4" />
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            type={showPassword ? "text" : "password"}
            className={cn(
              "w-full h-[46px] sm:h-12 lg:h-11 rounded-[12px] border bg-white px-4 text-sm transition-all duration-200",
              "focus:outline-none",
              "placeholder:text-[#A7B0C0]",
              showIcon && "pl-11",
              "pr-12",
              error
                ? "border-destructive"
                : "",
              className
            )}
            style={{
              borderColor: error ? undefined : '#E6EAF2',
              boxShadow: 'none',
              color: '#0B1220',
            }}
            onMouseEnter={(e) => {
              const target = e.target as HTMLInputElement;
              if (document.activeElement !== target && !error) {
                target.style.borderColor = '#D8E2F2';
              }
            }}
            onMouseLeave={(e) => {
              const target = e.target as HTMLInputElement;
              if (document.activeElement !== target && !error) {
                target.style.borderColor = '#E6EAF2';
              }
            }}
            onFocus={(e) => {
              if (!error) {
                e.target.style.boxShadow = 'none';
                e.target.style.borderColor = 'rgba(31, 107, 255, 0.65)';
              }
              props.onFocus?.(e);
            }}
            onBlur={(e) => {
              e.target.style.boxShadow = 'none';
              if (!error) {
                e.target.style.borderColor = '#E6EAF2';
              }
              setCapsLockOn(false);
              props.onBlur?.(e);
            }}
            onKeyUp={handleKeyUp}
            onKeyDown={handleKeyDown}
            aria-invalid={!!error}
            aria-describedby={error ? `${inputId}-error` : undefined}
            {...props}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-opacity hover:opacity-70 text-muted-foreground"
            aria-label={showPassword ? translateInterfaceText("Ocultar senha") : translateInterfaceText("Mostrar senha")}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>
        {capsLockOn && !error && (
          <p className="text-[12px] text-muted-foreground">
            {translateInterfaceText("Caps Lock está ativado")}</p>
        )}
        {error && (
          <p
            id={`${inputId}-error`}
            className="text-[13px] text-destructive"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

PasswordField.displayName = "PasswordField";
